import React from 'react';
import SessionStore from 'stores/SessionStore';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cargomatic/ui';

const canAdjust = (shipmentIsCurrent) => {
  const isFinanceUser = SessionStore.hasFinanceRole();
  return isFinanceUser || shipmentIsCurrent;
};

export default function AdjustmentActions({
  lineItem,
  shipmentIsCurrent,
  isIntermodalShipmentType,
  onAddCustomAdjustment,
  onAddShipperCostAdjustment,
  onAddInternalAdjustment,
  onAddCustomerAdjustment,
  onRemove,
  showRemove,
  onApprove,
  showApprove,
}) {
  const { t } = useTranslation();

  const adjustItems = [];
  const extraItems = [];

  if (canAdjust(shipmentIsCurrent)) {
    const { category, isFirstFuelSurchargeItem } = lineItem;
    if (category !== 'rate_adjustments' && category !== 'incentive_payments') {
      if (isIntermodalShipmentType) {
        if (!(lineItem.processed && lineItem.parent_adjustment_id)) {
          if (
            lineItem.category === 'base_cost' &&
            lineItem.adjusted_item === 'base_cost' &&
            !lineItem.isCreditMemo
          ) {
            if (
              lineItem.message !== 'Shipper Cost Adjustment' &&
              lineItem.hasInvoice &&
              onAddShipperCostAdjustment
            ) {
              adjustItems.push({
                label: t('SHIPPER_COST_ADJUSTMENT'),
                onClick: () => onAddShipperCostAdjustment(lineItem),
              });
            } else {
              adjustItems.push({
                label: t('ADD_ADJUSTMENT'),
                onClick: () => onAddCustomAdjustment(lineItem),
              });
            }
          }

          if (
            lineItem.category === 'accessorials' &&
            lineItem.type === 'fuel_surcharge' &&
            !lineItem.has_pending_state &&
            (isFirstFuelSurchargeItem || lineItem.parent_adjustment_id) &&
            !lineItem.accessorial_request_id
          ) {
            adjustItems.push({
              label: t('ADD_ADJUSTMENT'),
              onClick: () => onAddCustomAdjustment(lineItem),
            });
          }
        }
      } else {
        adjustItems.push({
          label: t('ADD_INTERNAL_ADJUSTMENT'),
          onClick: () => onAddInternalAdjustment(lineItem),
        });
        adjustItems.push({
          label: t('ADD_CUSTOMER_ADJUSTMENT'),
          onClick: () => onAddCustomerAdjustment(lineItem),
        });
      }
    }
  }

  if (showApprove && onApprove) {
    extraItems.push({
      label: t('APPROVE_CARRIER_COST'),
      onClick: onApprove,
      disabled: lineItem.approved,
    });
  }

  if (showRemove && onRemove) {
    extraItems.push({
      label: t('REMOVE_LINE_ITEM'),
      onClick: onRemove,
      disabled: lineItem.processed,
      destructive: true,
    });
  }

  const allItems = [...adjustItems, ...extraItems];
  if (allItems.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {adjustItems.map((item, index) => (
          <DropdownMenuItem key={`adj-${index}`} onSelect={item.onClick}>
            {item.label}
          </DropdownMenuItem>
        ))}
        {adjustItems.length > 0 && extraItems.length > 0 && (
          <DropdownMenuSeparator />
        )}
        {extraItems.map((item, index) => (
          <DropdownMenuItem
            key={`extra-${index}`}
            disabled={item.disabled}
            onSelect={item.onClick}
            className={item.destructive ? 'text-destructive' : ''}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
